// App.tsx
import { WalletMultiButton } from '@solana/wallet-adapter-react-ui';
import { useConnection, useWallet } from '@solana/wallet-adapter-react';
import { Transaction, PublicKey, SystemProgram } from '@solana/web3.js';
import { useState } from 'react';
import styled from 'styled-components';

const Container = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 2rem;
  min-height: 100vh;
  background: linear-gradient(135deg, #1a1a1a 0%, #2a2a2a 100%);
  color: white;
`;

const Card = styled.div`
  background: rgba(36, 36, 36, 0.8);
  backdrop-filter: blur(10px);
  padding: 2rem;
  border-radius: 16px;
  width: 100%;
  max-width: 600px;
  margin-top: 2rem;
  box-shadow: 0 8px 32px 0 rgba(31, 38, 135, 0.37);
  border: 1px solid rgba(255, 255, 255, 0.1);
  transition: transform 0.2s;
  box-sizing: border-box;  /* Add this */

  &:hover {
    transform: translateY(-2px);
  }
  overflow: hidden; /* Add this */
`;

const StyledTextArea = styled.textarea`
  width: 100%;
  min-height: 150px;
  padding: 1.2rem;
  margin: 1rem 0;
  border-radius: 12px;
  background: rgba(54, 54, 54, 0.7);
  color: #fff;
  border: 1px solid rgba(255, 255, 255, 0.1);
  resize: vertical;
  font-family: 'Courier New', monospace;
  font-size: 14px;
  line-height: 1.5;
  transition: all 0.2s;
  box-sizing: border-box;  /* Add this */

  &:focus {
    outline: none;
    border-color: #9945FF;
    box-shadow: 0 0 0 2px rgba(153, 69, 255, 0.2);
  }
`;

const ButtonGroup = styled.div`
  display: flex;
  gap: 1rem;
  margin: 1rem 0;  /* Changed from margin-top */
  width: 100%;      /* Add this */
  flex-wrap: wrap;
  justify-content: flex-start;  /* Add this */
`;

const Button = styled.button`
  background: linear-gradient(135deg, #9945FF 0%, #8752F3 100%);
  color: white;
  border: none;
  padding: 12px 24px;
  border-radius: 12px;
  cursor: pointer;
  font-weight: 600;
  font-size: 14px;
  transition: all 0.2s;
  min-width: 120px;
  
  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 5px 15px rgba(153, 69, 255, 0.4);
  }

  &:active {
    transform: translateY(0);
  }

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
`;

const TestButton = styled(Button)`
  background: linear-gradient(135deg, #45a0ff 0%, #4285f4 100%);
`;

const ClearButton = styled(Button)`
  background: linear-gradient(135deg, #ff4545 0%, #d83333 100%);
`;

const DecodedInfo = styled.pre`
  background: rgba(42, 42, 42, 0.7);
  padding: 1.2rem;
  border-radius: 12px;
  color: #00ff95;
  font-family: 'Courier New', monospace;
  font-size: 14px;
  line-height: 1.6;
  margin-top: 1rem;
  border: 1px solid rgba(255, 255, 255, 0.1);
  box-shadow: inset 0 2px 4px rgba(0, 0, 0, 0.2);
  
  /* Fix overflow */
  white-space: pre-wrap;
  word-wrap: break-word;
  max-width: 100%;
  overflow-x: auto;
  
  /* Add some spacing for better readability */
  padding-bottom: 1.5rem;
  margin-bottom: 1rem;
`;

const Title = styled.h1`
  font-size: 2rem;
  font-weight: 700;
  margin-bottom: 2rem;
  background: linear-gradient(135deg, #9945FF 0%, #45a0ff 100%);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
`;

const App = () => {
  const { connection } = useConnection();
  const { wallet, publicKey, signTransaction } = useWallet();
  const [txString, setTxString] = useState('');
  const [decodedTx, setDecodedTx] = useState<string>('');

  const decodeInstructionData = (programId: string, data: Buffer) => {
    // System Program
    if (programId === SystemProgram.programId.toBase58()) {
      // First 4 bytes are instruction type 
      const type = data.readUInt32LE(0);
      
      // Transfer instruction (type 2)
      if (type === 2) {
        const lamports = data.readBigUInt64LE(4);
        return {
          type: 'Transfer',
          lamports: lamports.toString(),
          humanReadable: `Transfer ${lamports.toString()} lamports`
        };
      }
    }
    return {
      type: 'Unknown',
      rawData: data.toString('hex')
    };
  };

  const decodeTransaction = (base64String: string) => {
    try {
      const transaction = Transaction.from(
        Buffer.from(base64String, 'base64')
      );
      
      const decoded = {
        feePayer: transaction.feePayer?.toBase58(),
        recentBlockhash: transaction.recentBlockhash,
        instructions: transaction.instructions.map(instruction => ({
          programId: instruction.programId.toBase58(),
          decodedData: decodeInstructionData(
            instruction.programId.toBase58(), 
            instruction.data
          ),
          keys: instruction.keys.map(key => ({
            pubkey: key.pubkey.toBase58(),
            isSigner: key.isSigner,
            isWritable: key.isWritable
          }))
        }))
      };

      setDecodedTx(JSON.stringify(decoded, null, 2));
    } catch (error) {
      setDecodedTx('Invalid transaction');
    }
  };

  const handleTxStringChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    const newValue = e.target.value;
    setTxString(newValue);
    if (newValue) {
      decodeTransaction(newValue);
    } else {
      setDecodedTx('');
    }
  };

  const createTestTransaction = async () => {
    if (!publicKey) return;

    const destinationPubkey = new PublicKey('A6dXNxJH3vGLJRhVFBm8vQkd2eRE2QXnYS5SXBWKLLCV');
    
    const transaction = new Transaction().add(
      SystemProgram.transfer({
        fromPubkey: publicKey,
        toPubkey: destinationPubkey,
        lamports: 10,
      })
    );

    const latestBlockhash = await connection.getLatestBlockhash();
    transaction.recentBlockhash = latestBlockhash.blockhash;
    transaction.feePayer = publicKey;

    const serializedTransaction = transaction.serialize({
      requireAllSignatures: false
    });

    setTxString(serializedTransaction.toString('base64'));
    decodeTransaction(serializedTransaction.toString('base64'));
  };

  const handleTransaction = async () => {
    try {
      if (!publicKey || !signTransaction) return;
      
      const recoveredTransaction = Transaction.from(
        Buffer.from(txString, 'base64')
      );

      const signed = await signTransaction(recoveredTransaction);
      const signature = await connection.sendRawTransaction(signed.serialize());
      
      console.log('Transaction sent:', signature);
    } catch (error) {
      console.error('Error:', error);
    }
  };

  const clearTransaction = () => {
    setTxString('');
    setDecodedTx('');
  };

  return (
    <Container>
      <Title>Solana Transaction Decoder</Title>
      <WalletMultiButton />
      
      {publicKey && (
        <Card>
          <ButtonGroup>
            <TestButton onClick={createTestTransaction}>
              Create Test Transaction
            </TestButton>
            <ClearButton onClick={clearTransaction}>
              Clear
            </ClearButton>
          </ButtonGroup>
          <StyledTextArea 
            value={txString}
            onChange={handleTxStringChange}
            placeholder="Enter base64 encoded transaction"
          />
          {decodedTx && (
            <DecodedInfo>
              {decodedTx}
            </DecodedInfo>
          )}
          <ButtonGroup>
            <Button onClick={handleTransaction}>
              Sign & Send Transaction
            </Button>
            <ClearButton onClick={clearTransaction}>
              Clear
            </ClearButton>
          </ButtonGroup>
        </Card>
      )}
    </Container>
  );
};

export default App;